import { reactive, readonly, computed, toRefs, markRaw } from "vue";

declare global {
    interface Window {
        orejimeConfig: object;
        orejime: Orejime;
        loadOrejime: (config: object) => void;
    }
}

interface Orejime {
    manager: OrejimeManager;
    show: () => void;
    hide: () => void;
    prompt: () => void;
}

interface NormalizedConsentState {
    [purpose: string]: boolean;
}

interface State {
    ready: boolean;
    raw: NormalizedConsentState | null;
    instance: Orejime | null;
}

interface OrejimeManager {
    acceptAll: () => void;
    declineAll: () => void;
    clearConsents: () => void;
    getAllConsents: () => NormalizedConsentState;
    setConsent: (purpose: string, value: boolean) => void;
    getConsent: (purpose: string) => boolean;
    on: (event: 'update'|'clear'|'dirty', callback: (updatedConsents: any, allConsents: object) => void) => void;
}

const consents = computed(() => state.raw || {});
const state: State = reactive({
    ready: false,
    raw: null,
    instance: null,
});

let orejimeManager: OrejimeManager|null = null;
let modalObserver: MutationObserver|null = null;

function normalize(orejimeState: object) {
    if (!orejimeState) return {};

    const result: NormalizedConsentState = {};

    for (const [key, value] of Object.entries(orejimeState)) {
        result[key] = !!value;
    }

    return result;
}

function waitForOrejime() {
    if (typeof window === "undefined") return;

    const check = () => {
        if (!window.orejime) {
            return false;
        }

        state.instance = markRaw(window.orejime);
        orejimeManager = state.instance.manager;

        if (!orejimeManager) return false;

        state.raw = normalize(orejimeManager.getAllConsents());
        state.ready = true;
        attachListeners();
        checkHash();

        return true;
    };

    // try immediate
    if (check()) {
        return;
    }

    // fallback polling (because Orejime is script-loaded)
    const interval = window.setInterval(() => {
        if (check()) {
            clearInterval(interval);
        }
    }, 50);

    // safety stop
    setTimeout(() => clearInterval(interval), 5000);
}

function setConsent(purpose: string, value: boolean) {
    if (!orejimeManager) {
        throw new Error('[Orejime] Not ready yet');
    }

    orejimeManager.setConsent(purpose, value);
}

function isAllowed(purpose: string): boolean {
    return Boolean(consents.value[purpose]);
}

function open() {
    if (!state.instance) {
        throw new Error('[Orejime] Not ready yet');
    }

    state.instance.prompt();
}

function checkHash() {
    if (typeof window !== 'undefined' && window.location.hash === '#cookies') {
        try {
            open();
        } catch (e) {
            // Orejime not ready yet, it will be opened once ready by waitForOrejime
        }
    }
}

function startModalObserver() {
    if (typeof document === 'undefined' || modalObserver) return;

    const getIsModalOpen = () => 
        document.documentElement.classList.contains('orejimeHtml-WithModalOpen') ||
        document.body.classList.contains('orejimeHtml-WithModalOpen') ||
        !!document.querySelector('.orejime-ModalOverlay');

    let modalWasOpen = getIsModalOpen();

    modalObserver = new MutationObserver(() => {
        const isModalOpen = getIsModalOpen();
        
        if (modalWasOpen && !isModalOpen) {
            if (window.location.hash === '#cookies') {
                history.replaceState(null, '', window.location.pathname + window.location.search);
            }
        }
        
        modalWasOpen = isModalOpen;
    });

    modalObserver.observe(document.documentElement, {
        attributes: true,
        childList: true,
        subtree: true,
        attributeFilter: ['class'],
    });
}

function attachListeners() {
    if (!orejimeManager) {
        throw new Error('[Orejime] Not ready yet');
    }
    
    const callback = (updatedConsents: any, allConsents: object) => {
        state.raw = normalize(allConsents);
    };

    orejimeManager.on?.('update', callback);
}

function init(): void {
    if (typeof window === 'undefined' || state.instance || modalObserver) {
        return
    }

    window.addEventListener('hashchange', checkHash);
    startModalObserver();

    waitForOrejime();
}

export function useOrejime() {
    init();

    return {
        setConsent,
        isAllowed,
        open,
        ...toRefs(readonly(state)),
        consents: readonly(consents),
    };
}
